// src/components/AreeDiControllo.jsx
import React, { useMemo } from "react";
import {
    Thermometer,
    ShieldCheck,
    PackageCheck,
    ClipboardList,
    Tag,
    ClipboardCheck,
} from "lucide-react";

function AreaCard({ tone, icon, topValue, topLabel, bottomTitle, onClick }) {
    const tones = {
        blue: {
            topBg: "bg-gradient-to-br from-[#EFF7FF] to-[#DEEFFF] border-[#CCECF9]",
            topText: "text-[#009EE3]",
            bottomBg: "bg-[#009EE3]",
            bottomText: "text-white",
            iconBg: "bg-[#C9E5FF]",
            iconText: "text-[#009EE3]",
        },
        green: {
            topBg: "bg-gradient-to-br from-[#EEFAF7] to-[#D8F5EE] border-[#B5EBDE]",
            topText: "text-[#059669]",
            bottomBg: "bg-[#059669]",
            bottomText: "text-white",
            iconBg: "bg-[#B3EFE1]",
            iconText: "text-[#059669]",
        },
        orange: {
            topBg: "bg-gradient-to-br from-[#FFFCF6] to-[#FFEDD8] border-[#FFD7B3]",
            topText: "text-[#FA8216]",
            bottomBg: "bg-[#FA8216]",
            bottomText: "text-white",
            iconBg: "bg-[#FFDFB8]",
            iconText: "text-[#FA8216]",
        },
        purple: {
            topBg: "bg-gradient-to-br from-[#FAF2FF] to-[#F7E9FF] border-[#F1D8FF]",
            topText: "text-[#B95DEE]",
            bottomBg: "bg-[#B95DEE]",
            bottomText: "text-white",
            iconBg: "bg-[#F1D8FF]",
            iconText: "text-[#B95DEE]",
        },
        pink: {
            topBg: "bg-gradient-to-br from-[#FFF5F5] to-[#FFE2E2] border-[#FFD0D2]",
            topText: "text-[#DF5254]",
            bottomBg: "bg-[#DF5254]",
            bottomText: "text-white",
            iconBg: "bg-[#FFD0D2]",
            iconText: "text-[#DF5254]",
        },
        slate: {
            topBg: "bg-gradient-to-br from-[#F9F9F9] to-[#E0E0E0] border-[#D8D8D8]",
            topText: "text-[#5C5C5C]",
            bottomBg: "bg-[#5C5C5C]",
            bottomText: "text-white",
            iconBg: "bg-[#D8D8D8]",
            iconText: "text-[#5C5C5C]",
        },
    };

    const t = tones[tone] || tones.slate;

    return (
        <button
            type="button"
            onClick={onClick}
            className={[
                "rounded-2xl overflow-hidden text-left bg-white",
                "shadow-xl hover:shadow-2xl transition-all hover:-translate-y-0.5",
                "focus:outline-none focus:ring-2 focus:ring-slate-300",
                "w-[200px] min-w-[200px] h-[160px]",
                "lg:w-full lg:min-w-0",
            ].join(" ")}
        >
            {/* TOP */}
            <div className={`h-[114px] px-4 pt-3 border ${t.topBg}`}>
                <div
                    className={[
                        "h-[33px] w-[33px] rounded-lg grid place-items-center",
                        t.iconBg,
                        t.iconText,
                    ].join(" ")}
                >
                    {icon}
                </div>

                <div className={`mt-2 text-[28px] font-semibold leading-none ${t.topText}`}>
                    {topValue}
                </div>

                <div
                    className={`mt-2 text-[13px] font-medium leading-[13px] ${t.topText}`}
                    title={topLabel}
                >
                    {topLabel}
                </div>
            </div>

            {/* BOTTOM */}
            <div className={`h-[46px] px-4 grid items-center ${t.bottomBg}`}>
                <div className={`text-[18px] font-normal leading-4 ${t.bottomText}`}>
                    {bottomTitle}
                </div>
            </div>
        </button>
    );
}

export default function AreeDiControllo({ data, onOpen = () => { } }) {
    const demoData = {
        cryo: { active24h: 8, total: 10 },
        sanif: { pct: 85, done: 24, total: 28 },
        merci: { count: 12 },
        abbatt: { done: 3, total: 5 },
        etichette: { count: 8 },
        operatori: { label: "8" },
    };

    const d = useMemo(() => {
        const x = data || {};

        const hasAnyRealData =
            x &&
            (
                x.cryo !== undefined ||
                x.sanif !== undefined ||
                x.merci !== undefined ||
                x.abbatt !== undefined ||
                x.etichette !== undefined ||
                x.operatori !== undefined
            );

        if (!hasAnyRealData) return demoData;

        return {
            ...demoData,
            ...x,
            cryo: { ...demoData.cryo, ...(x.cryo || {}) },
            sanif: { ...demoData.sanif, ...(x.sanif || {}) },
            merci: { ...demoData.merci, ...(x.merci || {}) },
            abbatt: { ...demoData.abbatt, ...(x.abbatt || {}) },
            etichette: { ...demoData.etichette, ...(x.etichette || {}) },
            operatori: { ...demoData.operatori, ...(x.operatori || {}) },
        };
    }, [data]);

    const cryoTopValue = `${Number(d.cryo?.active24h || 0)}/${Number(d.cryo?.total || 0)}`;
    const cryoTopLabel = "Dati ultime 12h";

    const sanifTopValue = `${Number(d.sanif?.pct || 0)}%`;
    const sanifTopLabel = `${Number(d.sanif?.done || 0)}/${Number(d.sanif?.total || 0)} completate`;

    const merciTopValue = `${Number(d.merci?.count || 0)}`;
    const merciTopLabel = "Merci registrate oggi";

    const abbattTopValue = `${Number(d.abbatt?.done || 0)}/${Number(d.abbatt?.total || 0)}`;
    const abbattTopLabel = "Completati oggi";

    const etichetteTopValue = `${Number(d.etichette?.count || 0)}`;
    const etichetteTopLabel = "Create oggi";

    const operatoriTopValue = `${d.operatori?.label ?? "0"}`;
    const operatoriTopLabel = "Verifiche oggi";

    return (
        <section>
            <div className="mb-3">
                <div className="text-[28px] font-light text-[#454545]">
                    Aree di controllo
                </div>
            </div>

            {/* MOBILE */}
            <div className="lg:hidden -mx-4 px-4">
                <div className="flex gap-4 overflow-x-auto pb-2 pr-2">
                    <AreaCard
                        tone="blue"
                        icon={<Thermometer size={20} />}
                        topValue={cryoTopValue}
                        topLabel={cryoTopLabel}
                        bottomTitle="Controllo Temperatura"
                        onClick={() => onOpen("temp")}
                    />

                    <AreaCard
                        tone="green"
                        icon={<ShieldCheck size={20} />}
                        topValue={sanifTopValue}
                        topLabel={sanifTopLabel}
                        bottomTitle="Sanificazione"
                        onClick={() => onOpen("sanificazione")}
                    />

                    <AreaCard
                        tone="orange"
                        icon={<PackageCheck size={20} />}
                        topValue={merciTopValue}
                        topLabel={merciTopLabel}
                        bottomTitle="Ricezione Merci"
                        onClick={() => onOpen("ricevimento")}
                    />

                    <AreaCard
                        tone="purple"
                        icon={<ClipboardList size={20} />}
                        topValue={abbattTopValue}
                        topLabel={abbattTopLabel}
                        bottomTitle="Abbattimenti"
                        onClick={() => onOpen("abbattimenti")}
                    />

                    <AreaCard
                        tone="pink"
                        icon={<Tag size={20} />}
                        topValue={etichetteTopValue}
                        topLabel={etichetteTopLabel}
                        bottomTitle="Etichette"
                        onClick={() => onOpen("etichette")}
                    />

                    <AreaCard
                        tone="slate"
                        icon={<ClipboardCheck size={20} />}
                        topValue={operatoriTopValue}
                        topLabel={operatoriTopLabel}
                        bottomTitle="Igiene Personale"
                        onClick={() => onOpen("igiene")}
                    />
                </div>
            </div>

            {/* DESKTOP */}
            <div className="hidden lg:block">
                <div
                    className={[
                        "grid gap-4",
                        "lg:[grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]",
                    ].join(" ")}
                >
                    <AreaCard
                        tone="blue"
                        icon={<Thermometer size={20} />}
                        topValue={cryoTopValue}
                        topLabel={cryoTopLabel}
                        bottomTitle="Controllo Temperatura"
                        onClick={() => onOpen("temp")}
                    />

                    <AreaCard
                        tone="green"
                        icon={<ShieldCheck size={20} />}
                        topValue={sanifTopValue}
                        topLabel={sanifTopLabel}
                        bottomTitle="Sanificazione"
                        onClick={() => onOpen("sanificazione")}
                    />

                    <AreaCard
                        tone="orange"
                        icon={<PackageCheck size={20} />}
                        topValue={merciTopValue}
                        topLabel={merciTopLabel}
                        bottomTitle="Ricezione Merci"
                        onClick={() => onOpen("ricevimento")}
                    />

                    <AreaCard
                        tone="purple"
                        icon={<ClipboardList size={20} />}
                        topValue={abbattTopValue}
                        topLabel={abbattTopLabel}
                        bottomTitle="Abbattimenti"
                        onClick={() => onOpen("abbattimenti")}
                    />

                    <AreaCard
                        tone="pink"
                        icon={<Tag size={20} />}
                        topValue={etichetteTopValue}
                        topLabel={etichetteTopLabel}
                        bottomTitle="Etichette"
                        onClick={() => onOpen("etichette")}
                    />

                    <AreaCard
                        tone="slate"
                        icon={<ClipboardCheck size={20} />}
                        topValue={operatoriTopValue}
                        topLabel={operatoriTopLabel}
                        bottomTitle="Igiene Personale"
                        onClick={() => onOpen("igiene")}
                    />
                </div>
            </div>
        </section>
    );
}